import { ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { LayoutDashboard, Table2, Users2, ShieldCheck, ClipboardCheck, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { ThemeToggle } from './ThemeToggle';

// Item de sidebar "Aurora" : rail d'icônes sur mobile, icône + label sur lg+.
const sideItem = ({ isActive }: Readonly<{ isActive: boolean }>) =>
  `side-item ${isActive ? 'side-item-active' : ''}`;

export function Layout({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-full flex">
      {/* Sidebar */}
      <aside
        className="w-16 lg:w-60 shrink-0 sticky top-0 h-screen flex flex-col border-r bd backdrop-blur-md"
        style={{ backgroundColor: 'var(--side-bg)' }}
      >
        {/* Marque */}
        <Link to="/" className="flex items-center gap-3 px-3 lg:px-5 h-16 border-b bd shrink-0">
          <img src="/favicon.png" alt="ZCE" className="h-9 w-auto shrink-0" />
          <span className="hidden lg:block">
            <span className="block font-heading font-semibold text-[15px] tracking-tight t-strong leading-tight">
              ZCE Backoffice
            </span>
            <span className="block text-[11px] t-faint">Flotte IoT</span>
          </span>
        </Link>

        {/* Navigation */}
        <nav className="flex-1 px-2 lg:px-3 py-4 space-y-1 overflow-y-auto">
          <NavLink to="/" end className={sideItem} title="Accueil">
            <LayoutDashboard className="h-[18px] w-[18px] shrink-0" />
            <span className="hidden lg:inline">Accueil</span>
          </NavLink>
          <NavLink to="/inventaire" className={sideItem} title="Vue generale">
            <Table2 className="h-[18px] w-[18px] shrink-0" />
            <span className="hidden lg:inline">Vue generale</span>
          </NavLink>
          <NavLink to="/clients" className={sideItem} title="Vue client">
            <Users2 className="h-[18px] w-[18px] shrink-0" />
            <span className="hidden lg:inline">Vue client</span>
          </NavLink>
          <NavLink to="/audit" className={sideItem} title="Audit">
            <ClipboardCheck className="h-[18px] w-[18px] shrink-0" />
            <span className="hidden lg:inline">Audit</span>
          </NavLink>
          {user?.role === 'admin' && (
            <NavLink to="/utilisateurs" className={sideItem} title="Utilisateurs">
              <ShieldCheck className="h-[18px] w-[18px] shrink-0" />
              <span className="hidden lg:inline">Utilisateurs</span>
            </NavLink>
          )}
        </nav>

        {/* Pied : thème + utilisateur */}
        <div className="border-t bd p-3 lg:p-4 shrink-0 space-y-2">
          <ThemeToggle />
          <div className="hidden lg:flex items-center justify-between gap-2">
            <span className="text-[12px] t-muted truncate" title={user?.email}>
              {user?.email}
            </span>
            <span className="badge-role">{user?.role}</span>
          </div>
          <button
            type="button"
            onClick={() => logout()}
            className="btn-ghost w-full justify-center lg:justify-start"
            title="Deconnexion"
          >
            <LogOut className="h-4 w-4 shrink-0" />
            <span className="hidden lg:inline">Deconnexion</span>
          </button>
        </div>
      </aside>

      {/* Contenu */}
      <main className="flex-1 min-w-0 px-4 lg:px-8 py-8">
        <div className="max-w-[1440px] mx-auto">{children}</div>
      </main>
    </div>
  );
}
